



















































import { Component, Mixins, Prop, Watch } from 'vue-property-decorator';
import moment, { Moment } from 'moment';

import Card from '@/components/UI/Card.vue';
import Toast from '@/mixins/toast';

import MmHintedButton from '../../components/MmHintedButton.vue';
import { createCancelSource } from '../../api/mmHttp';
import { fetchZoningUnresolved } from '../../api/zoning';
import { createPaginationState } from '../../api/pagination';
import { isCanceled, MmError, toMmError } from '../../core/errors';
import { MmZoningUnresolvedItem } from '../../types/dto';

const KEY = 'priceMonitoring.mobileMonitoring.zoning.';
const ISO_WIRE_FORMAT = 'YYYY-MM-DD[T]HH:mm:ss[Z]';
const DEFAULT_DAYS = 30;

@Component({
	components: {
		Card,
		MmHintedButton,
	},
})
export default class ZoningUnresolvedBanner extends Mixins(Toast) {
	readonly keyPrefix = KEY;

	@Prop({ required: true }) readonly tradingId!: number;

	@Prop({ default: true }) readonly isActive!: boolean;

	photosTotal: number = 0;

	unresolvedItems: MmZoningUnresolvedItem[] = [];

	isLoading: boolean = false;

	loadError: MmError | null = null;

	periodFrom: string = '';

	periodTo: string = '';

	appliedFrom: string = '';

	appliedTo: string = '';

	private cancelSource: import('axios').CancelTokenSource | null = null;

	get disabled(): boolean {
		return this.tradingId === null || this.tradingId <= 0;
	}

	get disabledReason(): string {
		return this.disabled ? (this.$t(KEY + 'networkRequired') as string) : '';
	}

	get periodRange(): [Moment, Moment] | null {
		if (this.periodFrom === '' || this.periodTo === '') {
			return null;
		}

		return [moment.utc(this.periodFrom), moment.utc(this.periodTo)];
	}

	get hasPendingPeriodChanges(): boolean {
		return this.periodFrom !== this.appliedFrom || this.periodTo !== this.appliedTo;
	}

	get columns(): Array<{ title: string; dataIndex: string; key: string }> {
		return [
			{
				title: this.$t(KEY + 'colCompetitor') as string,
				dataIndex: 'competitorName',
				key: 'competitorName',
			},
			{
				title: this.$t(KEY + 'colStores') as string,
				dataIndex: 'storesCount',
				key: 'storesCount',
			},
			{
				title: this.$t(KEY + 'colPhotos') as string,
				dataIndex: 'photosCount',
				key: 'photosCount',
			},
		];
	}

	get summaryText(): string {
		if (this.isLoading) {
			return this.$t(KEY + 'loading') as string;
		}

		if (this.loadError !== null) {
			return this.$t(KEY + 'summaryError') as string;
		}

		return this.$t(KEY + 'summary', { count: this.photosTotal }) as string;
	}

	created(): void {
		this.resetPeriodDefault();
	}

	beforeDestroy(): void {
		this.cancelPending();
	}

	@Watch('tradingId')
	onTradingIdChanged(): void {
		this.resetPeriodDefault();
		this.loadSummary();
	}

	@Watch('isActive')
	onActiveChanged(active: boolean): void {
		if (
			active
			&& this.photosTotal === 0
			&& this.unresolvedItems.length === 0
			&& this.loadError === null
		) {
			this.loadSummary();
		}
	}

	handlePeriodChange(dates: [Moment, Moment] | null, _dateStrings: string[]): void {
		if (!dates || dates.length !== 2) {
			this.periodFrom = '';
			this.periodTo = '';

			return;
		}

		// Бэкенду нужен момент времени в UTC, а не дата; второй аргумент @change — строки виджета
		const fromMoment = dates[0] ? dates[0].clone().utc().startOf('day') : null;
		const toMoment = dates[1] ? dates[1].clone().utc().endOf('day') : null;

		this.periodFrom = fromMoment ? fromMoment.format(ISO_WIRE_FORMAT) : '';
		this.periodTo = toMoment ? toMoment.format(ISO_WIRE_FORMAT) : '';
	}

	loadSummary(): void {
		if (this.disabled || !this.isActive) {
			return;
		}

		if (this.periodFrom === '' || this.periodTo === '') {
			return;
		}

		this.appliedFrom = this.periodFrom;
		this.appliedTo = this.periodTo;

		this.cancelPending();
		const source = createCancelSource();

		this.cancelSource = source;
		this.isLoading = true;
		this.loadError = null;
		this.unresolvedItems = [];

		const pagination = createPaginationState(100);

		fetchZoningUnresolved(
			{ from: this.appliedFrom, to: this.appliedTo },
			pagination,
			{ cancelToken: source.token },
		)
			.then((result) => {
				if (this.cancelSource !== source) {
					return;
				}

				this.photosTotal = result.photosTotal;
				this.unresolvedItems = result.page.content;
			})
			.catch((error) => {
				if (isCanceled(error) || this.cancelSource !== source) {
					return;
				}

				this.photosTotal = 0;
				this.unresolvedItems = [];
				this.loadError = toMmError(error, 'zoning/unresolved');
				this.showResponseError(error);
			})
			.finally(() => {
				if (this.cancelSource === source) {
					this.cancelSource = null;
					this.isLoading = false;
				}
			});
	}

	private resetPeriodDefault(): void {
		const to = moment().utc().endOf('day');
		const from = moment().utc().subtract(DEFAULT_DAYS - 1, 'days').startOf('day');

		this.periodFrom = from.format(ISO_WIRE_FORMAT);
		this.periodTo = to.format(ISO_WIRE_FORMAT);
		this.appliedFrom = this.periodFrom;
		this.appliedTo = this.periodTo;
	}

	private cancelPending(): void {
		if (this.cancelSource !== null) {
			this.cancelSource.cancel();
			this.cancelSource = null;
		}
	}
}
